import type { AxiosError, AxiosInstance } from 'axios';

import { RENEWABLE_AUTH_ERROR_CODES, AuthApiErrorCode } from '@mfw/common';
import axios from 'axios';

import type { SessionCookieGuard, SessionCookieStamp } from './sessionCookie';

import { markRenewalFailure } from './http/renewalFailure';
import { SessionMovedError } from './sessionCookie';
import { ForbiddenError } from './http';

/**
 * The three decisions this file owns — hold a request until nothing is being written under
 * it, replay one that was refused because its cookie was, and judge every auth failure
 * against the session that is current by the time it lands — live here rather than in
 * `api/axios.ts` because that module cannot be unit tested in this package's jest runner:
 * it reaches `src/config`, the Firebase singleton and the query-client loader at module
 * level.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * belongs to the credential transition (§4.9); passes its hold at the barrier and in
     * recovery
     */
    credentialTransition?: boolean;
    /** this request is already a replay — it never gets a second one */
    _retry?: boolean;
    /** the acting tenant the barrier confirmed against, for the header interceptor */
    sessionContextKey?: string;
    /** what the guard had confirmed when this request left */
    sessionCookieStamp?: SessionCookieStamp;
    /** this request IS the renewal; awaiting the barrier here would deadlock */
    skipSessionRenewal?: boolean;
  }
}

/**
 * Every code the auth layer answers with — not a second hand-written list: a code that
 * exists in the enum and not here would be surfaced as an ordinary 403, and the whole
 * stamp check below would step around the one family of failures it exists for.
 *
 * The split inside it is `RENEWABLE_AUTH_ERROR_CODES`, which lives in `@mfw/common` beside
 * the codes themselves and is shared with the toast predicate: a code that is replayed here
 * but toasted there is the wake-up burst this workstream exists to remove.
 */
const AUTH_ERROR_CODES: readonly string[] = Object.values(AuthApiErrorCode);

/**
 * What kind of answer is this, for the two questions the interceptor asks of it?
 *
 * `auth` — does it describe the SESSION? Those are the failures a cache subscriber signs a
 * user out on, so each one has to be judged against the session in force when it lands.
 * A billing lock, a permission refusal or an uncoded 403 says nothing about the session.
 *
 * `renewable` — can a new cookie fix it? `TENANT_MISMATCH` and `ACCOUNT_SUSPENDED` are auth
 * failures that no mint repairs; they take the stamp check and nothing else.
 */
function classifyAuthFailure(status: number | undefined, code: unknown) {
  // A 401 has no code of its own, and it is the app's sign-out trigger.
  if (status === 401) return { auth: true, renewable: true };
  if (status !== 403 || typeof code !== 'string') {
    return { auth: false, renewable: false };
  }
  return {
    auth: AUTH_ERROR_CODES.includes(code),
    renewable: RENEWABLE_AUTH_ERROR_CODES.some(
      (renewable) => renewable === code,
    ),
  };
}

/**
 * A request that asked for `responseType: 'blob'` — the two stats exports — gets its ERROR
 * body as a Blob as well, and `'code' in blob` is false for every one of them. So the one
 * 403 that exists to be renewed-and-replayed was invisible on exactly the requests a user
 * triggers by hand, and arrived instead as a toast with no text in it.
 *
 * Decoded ONCE, here, and written back over the Blob: everything downstream — the
 * classification, the typed error handed to `replayRefused`, and `handleErrors`, which
 * reads `code` and `message` straight off `response.data` — then sees one shape. Left as a
 * Blob, a terminal `SESSION_EXPIRED` reached the cache subscribers as an anonymous 403.
 *
 * Only JSON, and only on a failure. Anything else IS the payload, a spreadsheet is not
 * worth decoding, and a body that does not parse says nothing about the cookie — a parse
 * error thrown from here would replace the server's answer with one of our own making.
 */
async function normalizeErrorBody(error: AxiosError) {
  const response = error.response;
  if (!response) return;
  const data: unknown = response.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;
  if (!data.type.includes('json')) return;
  try {
    const parsed: unknown = JSON.parse(await readBlobText(data));
    // An error body is an OBJECT. A bare `null` parses fine and would reach
    // `handleErrors`, which reads `data.errors` off it — a TypeError of our own making in
    // place of the server's answer.
    if (typeof parsed !== 'object' || parsed === null) return;
    response.data = parsed;
  } catch {
    // Not JSON after all. The Blob stays exactly as the server sent it.
  }
}

/** The `code` field of an error body, once it is a plain object. */
function errorCodeOf(data: unknown): unknown {
  // Narrowed from `unknown`: an error body is whatever the server sent, and a permissions
  // 403 or a billing-lock 403 must never be replayed.
  return typeof data === 'object' && data !== null && 'code' in data
    ? (data as { code?: unknown }).code
    : undefined;
}

/**
 * `FileReader`, not `Blob.text()`. Both are fine in every browser this app supports, but
 * jsdom implements only the former — and a branch that cannot be run in the suite is a
 * branch that gets to be wrong. One path, and the one the test actually executes.
 */
function readBlobText(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * The refusal as a TYPED failure, the same shape `handleErrors` would have produced.
 *
 * `replayRefused` runs the shared refused-renewal predicate over what it is given, and that
 * predicate reads `code` off a `BaseApiResponseError`. Handed the raw `AxiosError` it
 * answered `false` for every code — so the one replay that is terminal, `SESSION_EXPIRED`,
 * was reported at error level with an on-screen notice saying the session could not be
 * renewed, for a session that is simply over.
 */
function typedRefusal(error: AxiosError) {
  const data: unknown = error.response?.data;
  const body = (typeof data === 'object' && data !== null ? data : {}) as {
    code?: string;
    message?: string;
  };
  return new ForbiddenError({ code: body.code, message: body.message ?? '' });
}

export function installSessionCookieInterceptors(
  client: AxiosInstance,
  guard: SessionCookieGuard<unknown>,
) {
  client.interceptors.request.use(async (request) => {
    // The renewal and the logout: both have to go out on the cookie they are about to
    // rewrite or delete, and awaiting the barrier here would deadlock the very mint it is
    // waiting for. They carry no stamp by design — their owners judge staleness for
    // themselves — and the response side leaves them alone for the same reason.
    if (request.skipSessionRenewal) return request;

    // What this request already belongs to, if it has been out once. A replay carries the
    // stamp of the dispatch that was refused.
    const previous = request.sessionCookieStamp;

    // THE BARRIER. Nothing leaves while a mint is being written or a credential is being
    // replaced — that is the fix: the old code fired the renewal and did not wait, so a
    // wake-up burst raced it and every request in the burst was rejected.
    //
    // The mark (auth-11 P2-2) belongs on THIS exit too, and not only on `recover`'s: when
    // the mint the barrier parked on fails, `join` rethrows that one shared error to every
    // waiter, and the mint `AuthProvider` starts on boot or on wake is a `renew` — no
    // recovery runs anywhere, so nothing else would mark it and the whole burst toasts.
    // A `SessionMovedError` from here is marked as well, harmlessly: it is not a
    // `BaseApiResponseError`, so the toast predicate never looks at it either way.
    const stamp = await guard
      .join({ transitionOwner: Boolean(request.credentialTransition) })
      .catch((error: unknown) => {
        markRenewalFailure(error);
        throw error;
      });

    // The last window in which a replay can go wrong: the session moved while this one was
    // at the barrier. Overwriting the stamp now would relabel a request issued under the
    // old login as belonging to the new one — and send it.
    if (previous !== undefined && !guard.isCurrent(previous)) {
      throw new SessionMovedError();
    }

    request.sessionCookieStamp = stamp;
    request.sessionContextKey = stamp.contextKey;
    return request;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);

      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);
      // The mint and the logout own their own outcome, and the guard already saw it
      // first-hand. Recovering one recursively re-mints in the middle of the sign-out it
      // was ending, and judging one by a stamp it never had rejects every refused mint as
      // stale before the guard can answer for it.
      if (originalRequest.skipSessionRenewal) return Promise.reject(error);

      await normalizeErrorBody(error);
      const failure = classifyAuthFailure(
        error.response?.status,
        errorCodeOf(error.response?.data),
      );
      // Not about the session at all. It belongs to its caller exactly as the server sent
      // it, whatever has happened to the session meanwhile.
      if (!failure.auth) return Promise.reject(error);

      // From here on this failure is a statement about the SESSION, and the cache
      // subscribers end one on it. It may only be surfaced if it still describes the
      // session and acting tenant in force right now — a replay judged by its generation
      // too, since a confirmation newer than the one it was refused for has replaced the
      // cookie it carried. An old session's refusal ending the new one is the same harm as
      // replaying an old request, in the other direction.
      if (
        !guard.isCurrent(
          originalRequest.sessionCookieStamp,
          Boolean(originalRequest._retry),
        )
      ) {
        return Promise.reject(new SessionMovedError());
      }

      if (originalRequest._retry) {
        // The replay itself, refused with the very answer it was replayed for. The renewal
        // resolved — so `recover()` reported nothing — and this code is kept out of the
        // toasts because it is "always replayed". The guard is the one place that can turn
        // it into a bounded retry and a single report. Not a 401 either: a persistent 401
        // is the app's sign-out trigger, not a silent death.
        if (failure.renewable && error.response?.status === 403) {
          guard.replayRefused(
            originalRequest.sessionCookieStamp,
            typedRefusal(error),
          );
        }
        return Promise.reject(error);
      }

      // An identity this backend will not bind, or an account that is gone. No mint
      // repairs either, and the caller's own sign-out is the designed answer.
      if (!failure.renewable) return Promise.reject(error);

      originalRequest._retry = true;
      // A failed recovery is the honest answer (§4.2 item 3): a typed NetworkError, a 5xx,
      // a Firebase code or SessionMovedError — never the refusal that triggered it, which
      // would tell the cache subscribers a session is over when only the mint was
      // unreachable, or hide a terminal renewal behind a renewable code. It is passed on
      // exactly as it arrived, type and identity untouched; the catch below only MARKS it
      // (auth-11 P2-2), so a consumer can tell the mint's own failure — which the guard has
      // already reported once, or deliberately kept quiet — from a failure of its request.
      const recovery = await guard
        .recover(originalRequest.sessionCookieStamp, {
          transitionOwner: Boolean(originalRequest.credentialTransition),
        })
        .catch((error: unknown) => {
          markRenewalFailure(error);
          throw error;
        });
      // DEFENCE IN DEPTH, and deliberately kept. Against the real guard neither branch
      // below can be taken: `recover` re-checks the entry epoch and context after every
      // await and throws `SessionMovedError` from `execute` before it can answer
      // `'stale-session'` to a caller whose session has moved. They exist because this
      // file is the layer that decides what a REPLAY is allowed to carry, and it may not
      // depend on another module's internal ordering to get that right — a guard that one
      // day resolves instead of throwing would otherwise replay an old request under a new
      // login. Both are exercised by the fake-guard cases in `sessionCookieAxios.spec.ts`;
      // that is what they are for, not dead code to simplify away.
      if (recovery === 'stale-session')
        return Promise.reject(new SessionMovedError());
      if (!guard.isCurrent(originalRequest.sessionCookieStamp))
        throw new SessionMovedError();
      return client(originalRequest);
    },
  );
}
